import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { isApiError } from '../../api/client'
import { keys } from '../../api/queries'
import { startErrorText, useJob, useMyJobs, useStartAnalyze, useToolsInfo, type AnalyzeResult } from '../../api/tools'
import { findAnalysisJob } from '../../lib/jobMatch'
import { instagramCode, parseLinks } from '../../lib/links'
import { useViewState } from '../../lib/navigation'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Spinner } from '../Spinner'
import { Choice } from './Choice'
import { JobProgress, JobResult, useOpenAnalysis } from './JobView'
import { LinksField } from './LinksField'

const usd = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 3 })

interface AnalyzeDialogProps {
  stateKey: string
  /** Ссылки из адреса (`?url=`, кнопка бота). */
  initial?: string
  /** Задача из адреса (`?job=`): окно открывается сразу с ней. */
  initialJob?: string | null
  /** Задача запущена или найдена — страница кладёт её в адрес. */
  onJob?: (id: string) => void
  onClose: () => void
}

/**
 * «Разбор видео»: ссылки на Reels → карточка (одна) или группа в избранном (несколько).
 * Кнопка бота второй раз (или перезагрузка Mini App) приводит к той же задаче: по
 * `?job=` или по тем же ссылкам среди задач за сутки; сервер тоже не заводит вторую.
 */
export function AnalyzeDialog({ stateKey, initial = '', initialJob = null, onJob, onClose: finishClose }: AnalyzeDialogProps) {
  const info = useToolsInfo()
  const start = useStartAnalyze()
  const myJobs = useMyJobs()
  const [text, setText] = useViewState(`${stateKey}.text`, initial)
  const [transcribe, setTranscribe] = useViewState(`${stateKey}.transcribe`, true)
  const [frames, setFrames] = useViewState(`${stateKey}.frames`, true)
  const [save, setSave] = useViewState(`${stateKey}.save`, false)
  const [jobId, setJobId] = useViewState<string | null>(`${stateKey}.job`, initialJob)
  const job = useJob(jobId)
  const open = useOpenAnalysis()
  const [openedJob, setOpenedJob] = useViewState<string | null>(`${stateKey}.opened`, null)
  const openedNow = useRef(openedJob)
  const onClose = () => {
    setText(''); setTranscribe(true); setFrames(true); setSave(false); setJobId(null); setOpenedJob(null)
    finishClose()
  }
  const qc = useQueryClient()
  const savedSeen = useRef(false)

  // Ссылки из кнопки бота уже разбирались за сутки — сразу их задача, без второго «Разобрать».
  const matched = useRef(false)
  useEffect(() => {
    if (matched.current || jobId || !initial.trim() || !myJobs.data) return
    matched.current = true
    const found = findAnalysisJob(myJobs.data, parseLinks(initial, instagramCode).ids)
    if (found) {
      setJobId(found.id)
      onJob?.(found.id)
    }
  }, [myJobs.data, jobId, initial, onJob])

  // Задача из адреса уже убрана (старше суток) — обратно к форме.
  useEffect(() => {
    if (jobId && isApiError(job.error, 404)) setJobId(null)
  }, [jobId, job.error])

  const parsed = useMemo(() => parseLinks(text, instagramCode), [text])
  const max = info.data?.analyze_max_urls ?? 20
  const count = parsed.ids.length
  const ready = count > 0 && parsed.bad.length === 0 && count <= max
  const price = info.data ? count * info.data.analyze_price_usd : null
  const profilePrice = info.data?.profile_price_usd
  const budget = info.data?.budget

  // Ролики легли в «Добавленные» — лента Залётов перечитается, когда до неё дойдут.
  const data = job.data
  useEffect(() => {
    if (data?.status !== 'done' || savedSeen.current) return
    if (!(data.result as AnalyzeResult).saved_post_ids?.length) return
    savedSeen.current = true
    void qc.invalidateQueries({ queryKey: keys.feedAll })
  }, [data, qc])

  useEffect(() => {
    if (!data || data.status !== 'done' || openedJob === data.id || openedNow.current === data.id) return
    const result = data.result as AnalyzeResult
    if (result.failed.length > 0 || (result.notes ?? []).length > 0) return
    openedNow.current = data.id
    setOpenedJob(data.id)
    open(result)
  }, [data, open, openedJob, setOpenedJob])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || start.isPending) return
    start.mutate(
      { urls: parsed.lines, transcribe, storyboard: frames, save },
      {
        onSuccess: (created) => {
          setJobId(created.id)
          onJob?.(created.id)
        },
      },
    )
  }

  const running = data?.status === 'queued' || data?.status === 'running'
  const finished = data?.status === 'done' || data?.status === 'failed'

  return (
    <Dialog
      title="Разбор видео"
      onClose={onClose}
      wide
      footer={
        jobId ? (
          <Button variant={finished ? 'primary' : 'ghost'} onClick={onClose}>
            {finished ? 'Готово' : 'Свернуть'}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" form="tools-analyze" disabled={!ready || start.isPending}>
              {start.isPending && <Spinner size={14} className="text-accent-fg" />}
              Разобрать
            </Button>
          </>
        )
      }
    >
      {!jobId ? (
        <form id="tools-analyze" onSubmit={submit} className="space-y-4">
          <p className="text-sm text-pretty text-muted">
            Вставьте ссылки на Reels из Instagram — по одной на строку. Для одной ссылки откроется обычный разбор, для
            нескольких — группа в избранном.
          </p>
          <LinksField
            label="Ссылки на видео"
            value={text}
            onChange={setText}
            parsed={parsed}
            placeholder={'https://www.instagram.com/reel/…\nhttps://www.instagram.com/reel/…'}
            hint={`По одной ссылке на строку, до ${max}`}
            max={max}
            kindLabel="пост Instagram"
          />
          <div className="flex flex-wrap gap-x-5">
            <Choice label="Транскрибировать" checked={transcribe} onChange={setTranscribe} />
            <Choice label="Разбить на кадры" checked={frames} onChange={setFrames} />
            <Choice label="Сохранить в Залёты" checked={save} onChange={setSave} />
          </div>
          {count > 0 && price !== null && (
            <p className="text-xs text-pretty text-muted">
              Новые посты берутся через Apify, до ${usd.format(price)}; посты, которые уже есть в заводе, — бесплатно.
              {save &&
                profilePrice !== undefined &&
                ` Для Залётов ещё до $${usd.format(profilePrice)} за подписчиков каждого нового автора.`}
              {budget && ` Сегодня потрачено $${usd.format(budget.spent_day)} из $${usd.format(budget.limit_day)}.`}
            </p>
          )}
          {budget && !budget.allowed && (
            <p className="text-xs text-danger">Лимит Apify исчерпан: разберутся только посты, которые уже есть в заводе.</p>
          )}
          {start.isError && (
            <p className="text-sm text-danger">{startErrorText(isApiError(start.error) ? start.error.detail : null)}</p>
          )}
        </form>
      ) : !data ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : running ? (
        <JobProgress job={data} />
      ) : (
        <JobResult job={data} beforeOpen={() => setOpenedJob(data.id)} />
      )}
    </Dialog>
  )
}
